import { useCallback, useMemo, useState } from "react";

export interface PaginationData {
  page?: number;
  pageSize?: number;
}

interface UpdateData {
  newPage?: number;
  newPageSize?: number;
}

interface UsePaginationResult<T> {
  data: T[];
  goToPage: (page: number) => void;
  updatePageData: (update: UpdateData) => void;
  currentPageData: PaginationData & {
    numPages: number;
    totalResults: number;
  };
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Hook used for paginating data on the client side.
 * @param data List of objects that needs to be paginated.
 * @param options settings for pagination
 */
export const usePagination = <T>(
  data: T[],
  options: PaginationData = {}
): UsePaginationResult<T> => {
  const [currPage, setCurrPage] = useState(options.page || DEFAULT_PAGE);
  const [currPageSize, setCurrPageSize] = useState(
    options.pageSize || DEFAULT_PAGE_SIZE
  );
  const numberOfPages = useMemo(
    () => Math.ceil(data.length / currPageSize),
    [data.length, currPageSize]
  );
  const paginatedData = useMemo(() => {
    const startIndex = (currPage - 1) * currPageSize;
    const endIndex = startIndex + currPageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currPage, currPageSize]);
  const goToPage = useCallback(
    (newPage: number) => {
      setCurrPage(Math.max(1, Math.min(newPage, numberOfPages)));
    },
    [numberOfPages]
  );
  const updatePageData = useCallback(
    ({ newPage, newPageSize }: UpdateData) => {
      if (newPageSize) {
        setCurrPageSize(newPageSize);
        setCurrPage(1);
      }
      if (newPage) {
        setCurrPage(Math.max(1, Math.min(newPage, numberOfPages)));
      }
    },
    [numberOfPages]
  );

  return {
    data: paginatedData,
    goToPage,
    updatePageData,
    currentPageData: {
      page: currPage,
      pageSize: currPageSize,
      numPages: numberOfPages,
      totalResults: data?.length || 0
    }
  };
};
export default usePagination;
